import React from 'react';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap, cx } from '@atlaskit/css';
import Image from '@atlaskit/image/image';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { CardAvatarFrame } from './CardAvatarFrame';
import { CardCover as LegacyCardCover } from './Legacy/CardCover';

export type CardCoverStyle = NonNullable<CardCoverProps['coverStyle']>;
export type CardCoverProps = React.ComponentProps<typeof LegacyCardCover> & {
	background?: React.ReactNode;
	/** Applies the disabled avatar treatment in the uplifted card. */
	isDisabled?: boolean;
};

import { CardCoverArtwork } from './CardCoverArtwork';

const styles = cssMap({
	cover: {
		gridArea: 'cover',
		position: 'relative',
		display: 'grid',
		placeItems: 'center',
		rowGap: token('space.200'),
		paddingBlockEnd: token('space.300'),
	},
	background: {
		position: 'absolute',
		insetBlockStart: token('space.negative.200'),
		insetBlockEnd: token('space.0'),
		insetInlineStart: token('space.negative.200'),
		insetInlineEnd: token('space.negative.200'),
		pointerEvents: 'none',
	},
	neutral: {
		backgroundColor: token('elevation.surface.sunken'),
	},
	gradient: {
		maskImage: 'linear-gradient(to bottom, black, transparent)',
	},
	cutout: {
		position: 'relative',
		boxSizing: 'border-box',
		marginBlockStart: token('space.negative.150'),
		width: '40px',
		height: '8px',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.full'),
	},
	avatar: {
		position: 'relative',
		display: 'flex',
	},
	disabledAvatar: {
		// Lift the gray toward the disabled treatment while keeping white details opaque.
		filter: 'grayscale(1) contrast(0.35) brightness(1.5)',
	},
	trailing: {
		position: 'absolute',
		insetBlockStart: token('space.0'),
		insetInlineEnd: token('space.0'),
		zIndex: 200,
		display: 'grid',
		justifyItems: 'end',
		alignItems: 'center',
		maxWidth: '100%',
	},
	layer: { gridArea: '1 / 1' },
});

const DefaultAvatar = ({ name, iconUrl }: { name: string; iconUrl?: string }): React.JSX.Element =>
	iconUrl ? (
		<Image src={iconUrl} alt={name} width={48} height={48} />
	) : (
		<CardAvatarFrame>
			<Avatar name={name} size="xlarge" />
		</CardAvatarFrame>
	);

export const CardCover = (props: CardCoverProps): React.JSX.Element => {
	if (!expVal('jira_browse_agent_modal_update', 'isEnabled', false)) {
		return <LegacyCardCover {...props} />;
	}

	const {
		name,
		avatar: AvatarComponent = DefaultAvatar,
		coverStyle = 'grid',
		coverColor,
		background,
		children,
		badge,
		iconUrl,
		isDisabled,
		testId,
	} = props;

	return (
		<Box xcss={styles.cover} testId={testId}>
			{background !== undefined ? (
				<Box xcss={styles.background} aria-hidden="true">
					{background}
				</Box>
			) : coverStyle !== 'gradient' ? (
				<Box xcss={cx(styles.background, styles.neutral)} aria-hidden="true">
					{coverStyle === 'grid' ? <CardCoverArtwork /> : null}
				</Box>
			) : (
				<Box
					xcss={cx(styles.background, styles.gradient)}
					style={{ backgroundColor: coverColor }}
					aria-hidden="true"
				/>
			)}
			<Box xcss={styles.cutout} data-card-cutout aria-hidden="true" />
			<Box xcss={cx(styles.avatar, isDisabled && styles.disabledAvatar)}>
				<AvatarComponent name={name} iconUrl={iconUrl} />
			</Box>
			{badge || children ? (
				<Box xcss={styles.trailing}>
					{badge ? <Box xcss={styles.layer}>{badge}</Box> : null}
					{children ? <Box xcss={styles.layer}>{children}</Box> : null}
				</Box>
			) : null}
		</Box>
	);
};
